import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  FileCode2,
  Globe2,
  Info,
  Layers,
  MessageCircle,
  MonitorSmartphone,
  Plug,
  ShieldCheck,
  Smartphone,
  TerminalSquare,
  Wand2,
  X,
} from "lucide-react";
import { FaWhatsapp as WhatsApp } from "react-icons/fa";

const WIDGET_SCRIPT_URL = "https://zaviramed.com.ng/widget.js";
const BACKEND_API_URL =
  "https://zaviramed-ce178a15d114.herokuapp.com/api/widget-leads";

const INSTALL_SNIPPET = `<script
  src="${WIDGET_SCRIPT_URL}"
  data-partner="my-clinic"
  data-source="website"
  data-heard-from="Partner website"
  async
></script>`;

const HTML_SNIPPET = `<!DOCTYPE html>
<html lang="en">
  <head>
    <title>My Website</title>
  </head>
  <body>
    <h1>Welcome to our clinic website</h1>

    <!-- Zaviramed Widget -->
    <script
      src="${WIDGET_SCRIPT_URL}"
      data-partner="my-clinic"
      data-source="website"
      data-heard-from="Partner website"
      async
    ></script>
  </body>
</html>`;

const REACT_SNIPPET = `import { useEffect } from "react";

function ZaviramedWidget() {
  useEffect(() => {
    if (document.getElementById("zaviramed-widget-script")) return;

    const script = document.createElement("script");
    script.id = "zaviramed-widget-script";
    script.src = "${WIDGET_SCRIPT_URL}";
    script.async = true;

    script.dataset.partner = "my-clinic";
    script.dataset.source = "react-website";
    script.dataset.heardFrom = "Partner website";

    document.body.appendChild(script);

    return () => {
      script.remove();
      window.__ZAVIRAMED_WIDGET_LOADED__ = false;
    };
  }, []);

  return null;
}

export default ZaviramedWidget;`;

const NEXT_SNIPPET = `"use client";

import { useEffect } from "react";

export default function ZaviramedWidget() {
  useEffect(() => {
    if (document.getElementById("zaviramed-widget-script")) return;

    const script = document.createElement("script");
    script.id = "zaviramed-widget-script";
    script.src = "${WIDGET_SCRIPT_URL}";
    script.async = true;

    script.dataset.partner = "my-clinic";
    script.dataset.source = "nextjs-website";
    script.dataset.heardFrom = "Partner website";

    document.body.appendChild(script);

    return () => {
      script.remove();
      window.__ZAVIRAMED_WIDGET_LOADED__ = false;
    };
  }, []);

  return null;
}`;

const WORDPRESS_SNIPPET = `<!-- Add this before the closing </body> tag or inside your theme footer -->
<script
  src="${WIDGET_SCRIPT_URL}"
  data-partner="my-wordpress-site"
  data-source="wordpress"
  data-heard-from="Partner website"
  async
></script>`;

const PHP_SNIPPET = `<?php
// Add this before the closing </body> tag in your PHP template.
?>

<script
  src="${WIDGET_SCRIPT_URL}"
  data-partner="my-php-site"
  data-source="php-website"
  data-heard-from="Partner website"
  async
></script>`;

const PAYLOAD_SNIPPET = `{
  "phoneNumber": "08012345678",
  "heardFrom": "Partner website",
  "partner": "my-clinic",
  "source": "website",
  "websiteUrl": "https://partnerwebsite.com/current-page",
  "userAgent": "Visitor browser details",
  "referrer": "https://google.com"
}`;

const ATTRIBUTES = [
  {
    name: "src",
    required: true,
    value: WIDGET_SCRIPT_URL,
    description: "Loads the Zaviramed floating WhatsApp widget.",
  },
  {
    name: "data-partner",
    required: true,
    value: "my-clinic",
    description:
      "Identifies the clinic, school, company, NGO, pharmacy, or website installing the widget.",
  },
  {
    name: "data-source",
    required: true,
    value: "website",
    description:
      "Identifies the channel where the lead came from, for example website, blog, school-portal, landing-page, or partner-page.",
  },
  {
    name: "data-heard-from",
    required: false,
    value: "Partner website",
    description:
      "Pre-filled attribution text sent with the lead. The user does not need to answer a separate question.",
  },
  {
    name: "async",
    required: false,
    value: "async",
    description:
      "Allows the script to load without blocking the website’s normal page rendering.",
  },
];

const STEPS = [
  {
    title: "Copy the script",
    text: "Use the script tag and change only the partner/source values.",
    icon: Clipboard,
  },
  {
    title: "Paste before </body>",
    text: "Add it near the bottom of the website so the page loads normally.",
    icon: FileCode2,
  },
  {
    title: "Test the floating button",
    text: "Open the website, click the widget, enter a WhatsApp number, and continue to chat.",
    icon: MonitorSmartphone,
  },
  {
    title: "Confirm lead tracking",
    text: "Check that partner, source, page URL, referrer, and phone number are saved.",
    icon: ShieldCheck,
  },
];

const TABS = [
  { id: "html", label: "HTML", icon: Code2, code: HTML_SNIPPET },
  { id: "react", label: "React", icon: Layers, code: REACT_SNIPPET },
  { id: "next", label: "Next.js", icon: Wand2, code: NEXT_SNIPPET },
  { id: "wordpress", label: "WordPress", icon: Globe2, code: WORDPRESS_SNIPPET },
  { id: "php", label: "PHP", icon: TerminalSquare, code: PHP_SNIPPET },
];

function CopyButton({ value, label = "Copy" }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-black text-slate-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied" : label}
    </button>
  );
}

function CodeBlock({ code }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-2xl shadow-slate-950/20">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-red-400" />
          <span className="h-3 w-3 rounded-full bg-yellow-400" />
          <span className="h-3 w-3 rounded-full bg-emerald-400" />
        </div>
        <CopyButton value={code} label="Copy code" />
      </div>

      <pre className="max-h-[520px] overflow-auto p-5 text-[12.5px] leading-6 text-slate-100 sm:text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function WidgetPreview() {
  return (
    <div className="relative mx-auto w-full max-w-sm">
      <div className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-2xl shadow-slate-900/10">
        <div className="rounded-[1.6rem] bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-600">
                Widget preview
              </p>
              <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
                Floating doctor chat
              </h3>
            </div>

            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-600/25">
              <WhatsApp size={25} />
            </div>
          </div>

          <div className="mt-8 rounded-[1.4rem] border border-slate-100 bg-white p-5 shadow-xl shadow-slate-900/5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
                Z
              </div>

              <div>
                <p className="font-black text-slate-950">
                  Talk to a Zaviramed Doctor
                </p>
                <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
                  Enter your WhatsApp number and continue to doctor chat.
                </p>
              </div>
            </div>

            <div className="mt-5">
              <label className="text-xs font-black text-slate-600">
                WhatsApp number
              </label>
              <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-400">
                080...
              </div>
            </div>

            <button
              type="button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3.5 text-sm font-black text-white shadow-lg shadow-emerald-600/25"
            >
              <WhatsApp size={18} />
              Continue to Doctor Chat
            </button>

            <p className="mt-3 text-center text-[11px] font-semibold leading-5 text-slate-400">
              No email field. No extra form. Just WhatsApp number and source
              tracking.
            </p>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-5 right-5 flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-xl shadow-emerald-600/30">
        <WhatsApp size={18} />
        Chat with a Doctor
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/90 backdrop-blur-2xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
        <a href="/" className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
            <img
              src="/logo_new_nobg.png"
              alt="Zaviramed"
              className="h-8 w-8 object-contain"
            />
          </div>

          <div>
            <p className="text-lg font-black leading-none text-slate-950">
              Zaviramed
            </p>
            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.22em] text-emerald-600">
              Developer
            </p>
          </div>
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          <a
            href="#install"
            className="text-sm font-black text-slate-600 hover:text-emerald-700"
          >
            Install
          </a>
          <a
            href="#examples"
            className="text-sm font-black text-slate-600 hover:text-emerald-700"
          >
            Examples
          </a>
          <a
            href="#attributes"
            className="text-sm font-black text-slate-600 hover:text-emerald-700"
          >
            Attributes
          </a>
          <a
            href="#payload"
            className="text-sm font-black text-slate-600 hover:text-emerald-700"
          >
            Payload
          </a>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="/developer"
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          >
            <ArrowLeft size={16} />
            Developer home
          </a>

          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700"
          >
            Main website
            <ExternalLink size={15} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="rounded-2xl bg-slate-100 p-3 text-slate-800 lg:hidden"
          aria-label="Open menu"
        >
          {open ? <X size={21} /> : <Code2 size={21} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-white px-5 pb-6 pt-3 lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-2">
            {[
              ["Install", "#install"],
              ["Examples", "#examples"],
              ["Attributes", "#attributes"],
              ["Payload", "#payload"],
            ].map(([label, href]) => (
              <a
                key={label}
                href={href}
                onClick={() => setOpen(false)}
                className="rounded-2xl px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
              >
                {label}
              </a>
            ))}

            <div className="mt-3 grid grid-cols-1 gap-3">
              <a
                href="/developer"
                className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700"
              >
                <ArrowLeft size={16} />
                Developer home
              </a>

              <a
                href="/"
                className="flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-black text-white"
              >
                Main website
                <ExternalLink size={15} />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default function WidgetConfig() {
  const [activeTab, setActiveTab] = useState("html");

  const activeExample = TABS.find((tab) => tab.id === activeTab) || TABS[0];

  return (
    <main className="min-h-screen bg-white text-slate-950">
      <style>{`
        html {
          scroll-behavior: smooth;
        }

        body {
          background: #ffffff;
        }

        .zavira-grid {
          background-image:
            linear-gradient(rgba(15, 23, 42, 0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15, 23, 42, 0.045) 1px, transparent 1px);
          background-size: 60px 60px;
        }
      `}</style>

      <Header />

      <section className="relative overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-white">
        <div className="zavira-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,white_35%,transparent_78%)]" />
        <div className="absolute -left-48 top-20 h-96 w-96 rounded-full bg-emerald-300/20 blur-3xl" />
        <div className="absolute -right-48 bottom-0 h-96 w-96 rounded-full bg-blue-300/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 lg:grid-cols-[1fr_0.8fr] lg:px-8 lg:py-28">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">
              <Plug size={14} />
              Website Widget
            </div>

            <h1 className="mt-7 max-w-3xl text-4xl font-black leading-[1.05] tracking-[-0.045em] text-slate-950 sm:text-6xl lg:text-7xl">
              Add Zaviramed doctor chat to any website.
            </h1>

            <p className="mt-7 max-w-2xl text-lg font-semibold leading-8 text-slate-600">
              Install one JavaScript snippet. Visitors click the floating
              WhatsApp button, enter their WhatsApp number, and continue to
              Zaviramed doctor chat. The widget tracks the partner website and
              source automatically.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#install"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-sm font-black text-white shadow-xl shadow-emerald-600/25 hover:bg-emerald-700"
              >
                Install widget
                <ArrowRight size={17} />
              </a>

              <a
                href="#examples"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-black text-slate-700 shadow-sm hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
              >
                View examples
                <Code2 size={17} />
              </a>
            </div>

            <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
              {[
                ["No email", "Only WhatsApp number"],
                ["Fast install", "One script tag"],
                ["Tracked leads", "Partner/source saved"],
              ].map(([title, text]) => (
                <div
                  key={title}
                  className="rounded-3xl border border-slate-100 bg-white/85 p-5 shadow-xl shadow-slate-900/5 backdrop-blur"
                >
                  <Check size={18} className="text-emerald-600" />
                  <p className="mt-3 font-black text-slate-950">{title}</p>
                  <p className="mt-1 text-xs font-bold text-slate-500">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <WidgetPreview />
        </div>
      </section>

      <section id="install" className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
                Quick install
              </p>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
                Paste this before the closing body tag.
              </h2>
              <p className="mt-5 text-base font-semibold leading-8 text-slate-600">
                Change the partner and source values to match the website
                installing the widget. Do not add an email field. The widget is
                designed to capture the visitor’s WhatsApp number and redirect
                them to doctor chat.
              </p>

              <div className="mt-8 grid gap-4">
                {STEPS.map(({ title, text, icon: Icon }, index) => (
                  <div
                    key={title}
                    className="flex gap-4 rounded-3xl border border-slate-100 bg-slate-50 p-5"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="font-black text-slate-950">
                        {index + 1}. {title}
                      </p>
                      <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
                        {text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <CodeBlock code={INSTALL_SNIPPET} />
          </div>
        </div>
      </section>

      <section id="examples" className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
              Implementation examples
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
              Works with HTML, React, Next.js, WordPress, and PHP.
            </h2>
            <p className="mt-5 text-base font-semibold leading-8 text-slate-600">
              The script creates the floating button, opens the form, submits
              the WhatsApp number, and redirects the user to Zaviramed on
              WhatsApp.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {TABS.map(({ id, label, icon: Icon }) => {
              const active = activeTab === id;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                    active
                      ? "bg-emerald-600 text-white shadow-xl shadow-emerald-600/20"
                      : "border border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                  }`}
                >
                  <Icon size={16} />
                  {label}
                </button>
              );
            })}
          </div>

          <div className="mt-8">
            <CodeBlock code={activeExample.code} />
          </div>
        </div>
      </section>

      <section id="attributes" className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.75fr_1fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
                Configuration
              </p>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
                Script attributes explained.
              </h2>
              <p className="mt-5 text-base font-semibold leading-8 text-slate-600">
                These attributes tell Zaviramed where the patient came from.
                This is better than asking the user “how did you hear about us?”
                because the partner website already provides that context.
              </p>

              <div className="mt-6 rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
                <div className="flex gap-3">
                  <Info size={20} className="mt-0.5 shrink-0 text-emerald-700" />
                  <p className="text-sm font-bold leading-7 text-emerald-900">
                    Recommended: use a clear partner value like{" "}
                    <span className="font-black">university-of-portharcourt</span>,{" "}
                    <span className="font-black">abc-clinic</span>, or{" "}
                    <span className="font-black">nextafrica-foundation</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-2xl shadow-slate-900/5">
              <div className="hidden grid-cols-[0.8fr_0.55fr_1.4fr] gap-4 border-b border-slate-100 bg-slate-50 px-5 py-4 text-xs font-black uppercase tracking-widest text-slate-500 md:grid">
                <div>Attribute</div>
                <div>Example</div>
                <div>Purpose</div>
              </div>

              <div className="divide-y divide-slate-100">
                {ATTRIBUTES.map((item) => (
                  <div
                    key={item.name}
                    className="grid gap-3 px-5 py-5 md:grid-cols-[0.8fr_0.55fr_1.4fr] md:gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-black text-slate-800">
                          {item.name}
                        </code>
                        {item.required && (
                          <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                            Required
                          </span>
                        )}
                      </div>
                    </div>

                    <code className="break-all rounded-xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
                      {item.value}
                    </code>

                    <p className="text-sm font-semibold leading-6 text-slate-600">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="payload" className="bg-slate-950 py-20 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1fr]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-400">
                What the widget sends
              </p>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-white sm:text-5xl">
                Simple lead payload. No email required.
              </h2>
              <p className="mt-5 text-base font-semibold leading-8 text-slate-300">
                When a visitor submits the widget, Zaviramed receives their
                WhatsApp number, the partner/source configuration, the current
                website URL, browser user agent, and referrer. The visitor is
                then redirected to WhatsApp to continue the consultation.
              </p>

              <div className="mt-8 grid gap-4">
                {[
                  "The patient enters only their WhatsApp number.",
                  "The website supplies partner/source automatically.",
                  "The redirect message includes attribution for tracking.",
                  "The widget should be tested on desktop and mobile.",
                ].map((item) => (
                  <div key={item} className="flex gap-3">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                      <Check size={14} className="text-emerald-400" />
                    </div>
                    <p className="font-semibold leading-7 text-slate-200">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <CodeBlock code={PAYLOAD_SNIPPET} />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-[2.5rem] bg-gradient-to-br from-emerald-600 to-emerald-700 p-8 text-white shadow-2xl shadow-emerald-600/20 sm:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-white">
                  <Smartphone size={14} />
                  Ready to install
                </div>

                <h2 className="mt-5 max-w-2xl text-3xl font-black tracking-tight sm:text-5xl">
                  One snippet. One WhatsApp number field. Clear partner
                  tracking.
                </h2>

                <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-emerald-50">
                  Add the widget to partner websites, school portals, pharmacy
                  pages, NGO campaign pages, and company wellness pages.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <CopyButton value={INSTALL_SNIPPET} label="Copy install script" />

                <a
                  href="/developer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-emerald-700 shadow-sm hover:bg-emerald-50"
                >
                  Developer home
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}